import React from "react";
import { Organization } from "@/types";
import { useLanguage } from "@/contexts/LanguageContext";
import formatCO2eCredits from "@/utils/formatAmountToCO2Credits";

interface OrganizationCardProps {
  organization: Organization;
  index: number;
}

const OrganizationCard = ({ organization, index }: OrganizationCardProps) => {
  const { t, language } = useLanguage();

  // Animation delay based on index for staggered appearance
  const animationDelay = `${index * 50}ms`;

  // Get country flag emoji from country code
  const getCountryFlag = (countryCode?: string) => {
    if (!countryCode) return "🌎";

    // Convert country code to flag emoji (uses regional indicator symbols)
    const codePoints = countryCode
      .toUpperCase()
      .split("")
      .map((char) => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  };

  return (
    <div
      className="bg-white rounded-xl border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-md h-full"
      style={{
        animationDelay,
      }}
    >
      <div className="p-6 relative">
        {/* Organization logo (top right) - reduced height to 20px */}
        {organization.logoUrl && (
          <div className="absolute top-6 right-6">
            <img
              src={organization.logoUrl}
              alt={organization.title}
              className="h-[20px] w-auto object-contain"
            />
          </div>
        )}

        {/* Organization title - repositioned below logo */}
        <div className="mt-12">
          <h3 className="mb-2 font-semibold text-lg">{organization.title}</h3>

          {/* Country flag and name - adjusted for position and font size */}
          {organization.countryCode && (
            <div className="flex items-center gap-2 text-sm mb-6">
              <span
                className="text-lg"
                title={organization.countryCode?.toUpperCase()}
              >
                {getCountryFlag(organization.countryCode)}
              </span>
              <span>
                {t(`countries.${organization.countryCode.toLowerCase()}`)}
              </span>
            </div>
          )}
        </div>

        {/* Project count and risk buffer - made font weight semi-bold */}
        <div className="flex flex-wrap gap-3 mt-4">
          {organization.projectCount !== undefined && (
            <div className="bg-gray-100 text-gray-800 rounded-md py-2 px-3 text-sm inline-flex">
              {t("organizations.projectCount")}:{" "}
              <span className="font-semibold ml-1">
                {organization.projectCount}
              </span>
            </div>
          )}

          {organization.riskBufferAmount !== undefined && (
            <div className="bg-gray-100 text-gray-800 rounded-md py-2 px-3 text-sm inline-flex">
              {t("organizations.riskBuffer")}:{" "}
              <span className="font-semibold ml-1">
                {formatCO2eCredits(organization.riskBufferAmount, t, language)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrganizationCard;
